import axios from "axios";
import { SearchFilters, SearchResponse } from "../types/search";
import { logger } from "../utils/logger";
import { getImageUrlsFromMedia } from "../utils/imageUtils";
import { createCloudRunAxios } from "./axios";

// Server-side cap on results returned in a single search request.
// 1000 covers full-page direct searches (no query); regular keyword
// searches typically return far fewer.
const DEFAULT_SEARCH_RESULT_LIMIT = 1000;
const SIMILAR_PROPERTIES_LIMIT = 10;

const apiClient = createCloudRunAxios("aiSearch");

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 429) {
      logger.error("Search rate limit exceeded", { status: 429, error });
      throw new Error("Search rate limit exceeded. Please try again later.");
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
      logger.error("Authentication error", {
        status: error.response?.status,
        error,
      });
      throw new Error(
        "API key invalid or unauthorized. Please check your credentials."
      );
    }
    if (error.response?.status === 503) {
      logger.error("Service unavailable", { status: 503, error });
      throw new Error(
        "Search service is temporarily unavailable. Please try again later."
      );
    }
    logger.error("Unexpected error", { error });
    throw error;
  }
);

//Transform New API
const transformMLSResponse = (property: any) => {
  const {
    address = "",
    basement = false,
    bathrooms = 0,
    bedrooms = 0,
    city = "",
    city_view = false,
    garage = false,
    garagespaces = 0,
    listingid = "",
    listingkey = "",
    listofficename = "",
    listprice = "0",
    listpricelow = "0",
    livingarea = "0",
    media = [],
    mountain_view = false,
    newconstructionyn = false,
    originatingsystemname = "",
    park_view = false,
    pool = false,
    postalcode = "",
    price = "0",
    propertysubtype = "Property",
    rentalyn = false,
    standardstatus = "Active",
    mlsstatus: mlsstatusRaw = "",
    state = "",
    subdivisionname = "",
    vector_match_score = 0,
    water_view = false,
    waterfront = false,
    yearbuilt = null,
    latitude,
    longitude,
    // RESO fields
    internetaddressdisplayyn = true, // When False, property must be displayed but cannot display street address
    // internetautomatedvaluationdisplayyn = true, // Can be skipped if not including valuations or price estimates
    // internetconsumercommentyn = true, // Can be skipped if the product doesn't allow for comments
    customfields = {},
  } = property || {};

  const squareFeet = parseFloat(livingarea) || 0;
  const propertyType = propertysubtype;

  // Extract specific MLS display rules
  const customFields = customfields || {};

  const mustRemovePhotos = customFields.NWM_IDXMustRemovePhotosYN === true;
  const mustRemovePrimaryPhoto =
    customFields.NWM_IDXMustRemovePrimaryPhotoYN === true;
  const showMapLink = !(customFields.NWM_ShowMapLink === false);

  return {
    id: listingkey,
    mlsNumber: listingid,
    title: `${propertyType} in ${city}${state ? `, ${state}` : ""}`,
    price: parseFloat(price) || 0,
    propertyType: propertyType,
    lotSizeSquareFeet: squareFeet,
    internetAddressDisplayYN: internetaddressdisplayyn,
    customFields: customfields,
    displayRules: {
      mustRemovePhotos,
      mustRemovePrimaryPhoto,
      showMapLink,
    },
    address: {
      full: address
        ? `${address}, ${city}, ${state} ${postalcode}`
        : `${city}, ${state} ${postalcode}`,
      city,
      state,
      zipcode: postalcode,
    },
    property: {
      latitude,
      longitude,
      bathroomsTotal: bathrooms || 0,
      bedroomsTotal: bedrooms || 0,
      garageSpaces: garage,
      hasBasement: basement,
      hasPool: pool,
      isCityView: city_view,
      isMountainView: mountain_view,
      isParkView: park_view,
      isWaterFront: waterfront,
      isWaterView: water_view,
      isNewConstruction: newconstructionyn,
      isRental: rentalyn,
      livingArea: squareFeet || 0,
      listprice: listprice || "",
      listpricelow: listpricelow || "",
      lotSizeSquareFeet: squareFeet || 0,
      propertySubType: propertysubtype,
      propertyType: propertyType,
      stories: null,
      subdivisionName: subdivisionname,
      yearBuilt: yearbuilt ? parseInt(yearbuilt, 10) : null,
    },
    details: {
      bedrooms: bedrooms || 0,
      bathrooms: bathrooms || 0,
      squareFeet: squareFeet || 0,
    },
    bedrooms: bedrooms || 0,
    bathrooms: bathrooms || 0,
    squareFeet: squareFeet || 0,
    description: "",
    images: getImageUrlsFromMedia(media, {
      mustRemovePhotos,
      mustRemovePrimaryPhoto,
      maxImages: 1,
      debugLog: true,
    }),
    features: [
      propertysubtype,
      pool && "Pool",
      basement && "Basement",
      waterfront && "Waterfront",
      city_view && "City View",
      mountain_view && "Mountain View",
      park_view && "Park View",
      water_view && "Water View",
      garage && (garagespaces > 0 ? `${garagespaces} Garage` : "Garage"),
      newconstructionyn && "New Construction",
      rentalyn && "Rental",
    ].filter(Boolean),
    listingStatus: standardstatus,
    listingOffice: {
      name: listofficename || "",
      email: "",
      phone: "",
      id: "",
      key: "",
    },
    mlsBoardCode: originatingsystemname || "",
    mlsstatus: Array.isArray(mlsstatusRaw)
      ? mlsstatusRaw[0] || ""
      : mlsstatusRaw || "",
    score: vector_match_score || 0,
    yearBuilt: yearbuilt ? parseInt(yearbuilt, 10) : null,
    stories: null,
    subdivisionName: subdivisionname,
    garageSpaces: garage,
  };
};

export const searchService = {
  async searchProperties(
    filters: SearchFilters,
    resultIndex: number = 0,
    pageSize: number = 20,
    signal?: AbortSignal
  ): Promise<SearchResponse> {
    try {
      const requestId = `search-${Date.now()}`;
      const { query, location, tenant_id, offset, ...restFilters } = filters;

      const searchParams = {
        ...(query ? { query } : {}),
        filters: restFilters.filters || {},
        location,
        tenant_id,
        number: filters.number || DEFAULT_SEARCH_RESULT_LIMIT,
        ...(offset !== undefined ? { offset } : {}),
      };

      logger.debug("Making search request", {
        url: `${apiClient.defaults.baseURL}/search`,
        params: searchParams,
        requestId,
      });

      const response = await apiClient.post("/search", searchParams, { signal });

      // Handle new API response format only
      if (
        !response.data ||
        (response.data.count === undefined &&
          response.data.total_count === undefined) ||
        (response.data.count === null && response.data.total_count === null) ||
        !Array.isArray(response.data.results)
      ) {
        logger.error("Invalid search response", { requestId, response });
        throw new Error("No properties found matching your criteria");
      }

      const properties = response.data.results.map(transformMLSResponse);

      // Determine if this is a direct search (no query) or regular search
      const isDirectSearch = !query;

      // For direct search, use total_count if available
      // For regular search, use count
      // This ensures we use the right count field for each type of search
      const totalMatches = isDirectSearch
        ? response.data.total_count || response.data.count || 0
        : response.data.count || response.data.total_count || 0;

      return {
        properties,
        total: totalMatches,
        resultIndex,
        pageSize,
        recordCount: totalMatches,
        resultCount: properties.length,
        totalMatches,
        hasMore: resultIndex + properties.length < totalMatches,
        timestamp: new Date().toISOString(),
        diagnosticInfo: {
          requestId,
          timestamp: new Date().toISOString(),
          searchParams: {
            ...searchParams,
            currentResults: properties.length,
            totalAvailable: totalMatches,
          },
        },
      };
    } catch (error) {
      // A stale search aborted by React Query is not a real failure.
      if (axios.isCancel(error)) {
        throw error;
      }
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          logger.error("Network error", { error });
          throw new Error("Network error. Please check your connection.");
        }
        logger.error("Search API error", {
          status: error.response.status,
          data: error.response.data,
          error,
        });
        throw new Error(
          error.response.data?.message || "Error searching properties"
        );
      }
      logger.error("Unexpected error during search", { error });
      throw error;
    }
  },

  async getSimilarProperties(propertyId: string, signal?: AbortSignal) {
    try {
      const response = await apiClient.post(
        "/search",
        {
          listing_id: propertyId,
          similar: true,
          number: SIMILAR_PROPERTIES_LIMIT,
        },
        { signal }
      );

      if (
        !response.data ||
        !response.data.results ||
        !Array.isArray(response.data.results)
      ) {
        logger.warn("No similar properties found", { propertyId });
        return [];
      }

      return response.data.results.map(transformMLSResponse);
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          logger.error("Network error fetching similar properties", {
            propertyId,
            error,
          });
          throw new Error("Network error. Please check your connection.");
        }
        logger.error("Similar properties API error", {
          propertyId,
          status: error.response.status,
          data: error.response.data,
          error,
        });
        throw new Error(
          error.response.data?.message || "Error fetching similar properties"
        );
      }
      logger.error("Unexpected error fetching similar properties", {
        propertyId,
        error,
      });
      throw error;
    }
  },
};
